import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface WorkflowStep {
  number: number;
  title: string;
  description: string;
  icon: string;
  duration: string;
}

const ProcessWorkflow = () => {
  const workflowSteps: WorkflowStep[] = [
    {
      number: 1,
      title: 'Discovery & Assessment',
      description:
        'We analyze your business needs, technical requirements, and project goals to create a tailored solution roadmap.',
      icon: 'MagnifyingGlassIcon',
      duration: '1-2 weeks',
    },
    {
      number: 2,
      title: 'Solution Design',
      description:
        'Our experts design the architecture, select technologies, and create detailed implementation plans with clear milestones.',
      icon: 'PencilSquareIcon',
      duration: '2-3 weeks',
    },
    {
      number: 3,
      title: 'Development & Implementation',
      description:
        'Agile development with regular sprints, continuous integration, and transparent progress tracking throughout the project.',
      icon: 'CodeBracketIcon',
      duration: '4-12 weeks',
    },
    {
      number: 4,
      title: 'Testing & Quality Assurance',
      description:
        'Comprehensive testing including unit, integration, and user acceptance testing to ensure flawless functionality.',
      icon: 'CheckBadgeIcon',
      duration: '1-2 weeks',
    },
    {
      number: 5,
      title: 'Deployment & Training',
      description:
        'Smooth production deployment with comprehensive team training and documentation for seamless adoption.',
      icon: 'RocketLaunchIcon',
      duration: '1 week',
    },
    {
      number: 6,
      title: 'Support & Optimization',
      description:
        'Ongoing support, performance monitoring, and continuous optimization to maximize value and ROI.',
      icon: 'WrenchScrewdriverIcon',
      duration: 'Ongoing',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Our Proven Process
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            A structured approach that ensures successful delivery and measurable
            outcomes
          </p>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-accent transform -translate-x-1/2"></div>

          <div className="space-y-12">
            {workflowSteps.map((step, index) => (
              <div
                key={step.number}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div
                  className={`w-full md:w-5/12 ${
                    index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'
                  }`}
                >
                  <div className="bg-card rounded-lg shadow-md hover:shadow-lg transition-all duration-base p-6 border border-border">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon
                          name={step.icon as any}
                          size={24}
                          variant="solid"
                          className="text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-headline font-bold text-foreground">
                            {step.title}
                          </h3>
                          <span className="px-3 py-1 bg-brand-light text-brand-blue text-xs font-headline font-semibold rounded-full">
                            {step.duration}
                          </span>
                        </div>
                        <p className="text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full items-center justify-center shadow-lg z-10">
                  <span className="text-2xl font-headline font-bold text-white">
                    {step.number}
                  </span>
                </div>

                <div className="hidden md:block w-5/12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessWorkflow;